import React, { useCallback, useMemo } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { useTableData } from 'src/serverProxy/useTableData';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import ReactDOMServer from 'react-dom/server';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const ReviewField = {
  QualityScore: 'reviewQualityScore',
  WordCountQualityScore: 'wordCountQualityScore',
  KeywordCountQualityScore: 'keywordCountQualityScore',
  ImageScore: 'imageScore',
  VideoScore: 'videoScore'
};

const ReviewDisplayName = {
  reviewQualityScore: 'Quality Score',
  wordCountQualityScore: 'Word Count',
  keywordCountQualityScore: 'Keyword Count',
  imageScore: 'Image Score',
  videoScore: 'Video Score'
};

const COLUMN_CHART_WIDTH = 640;

/**
 * A donut chart and topEntity chart that can be found on review tab.
 */
// Combined two graph (donut and topEntity chart) since their requests are identical
const ReviewDonutAndTopEntityChart = () => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const { getValuesByFieldName, isLoading, getComparisonValuesByFieldName } = useTableData({
    fields: [
      {
        name: ReviewField.QualityScore
      },
      {
        name: ReviewField.WordCountQualityScore
      },
      {
        name: ReviewField.KeywordCountQualityScore
      },
      {
        name: ReviewField.ImageScore
      },
      {
        name: ReviewField.VideoScore
      }
    ],
    groupByFieldName: 'retailerId',
    indexName: 'review-metrics',
    sortFieldName: ReviewField.QualityScore,
    itemsPerPage: 10000,
    pageNumber: 1,
    requestId: 'review-analysis-donutChart-and-topEntityChart'
  });

  const getQualityValue = useCallback(
    (fieldName: string): number => getValuesByFieldName(fieldName)[0] || 0,
    [getValuesByFieldName]
  );

  const getComparisonQualityValue = useCallback(
    (fieldName: string): number => getComparisonValuesByFieldName(fieldName)[0] || 0,
    [getComparisonValuesByFieldName]
  );

  const qualityScore = useMemo(() => getQualityValue(ReviewField.QualityScore), [getQualityValue]);

  const reviewQualityScores = [
    ReviewField.WordCountQualityScore,
    ReviewField.KeywordCountQualityScore,
    ReviewField.ImageScore,
    ReviewField.VideoScore
  ];
  const currentData = reviewQualityScores.map(
    (scoreField) => [ReviewDisplayName[scoreField], getQualityValue(scoreField)] as [string, number]
  );

  const compareData = reviewQualityScores.map(
    (compareScoreField) =>
      [ReviewDisplayName[compareScoreField], getComparisonQualityValue(compareScoreField)] as [string, number]
  );

  return (
    <Flex flexDirection="column" gap="xxl">
      <Flex alignItems="center" gap={108}>
        <BeaconChartWithLegend
          title="Review Quality Score"
          includeSummaryInfo={false}
          subtitle={
            <BeaconSubtitleContainer>
              <Text variant="body1">
                {`The chart displays the review quality score for ${entityPhrase} `}
                {taxonomyText}{' '}
                {`during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}`}
                .
                <SubtitleFilters />
              </Text>
            </BeaconSubtitleContainer>
          }
          showExportMenu={false}
        >
          <Flex marginTop="8px">
            <div style={{ width: '308px', height: '308px' }}>
              {isLoading ? <DonutChartLoading /> : <CircularProgressPercentage percentage={qualityScore} />}
            </div>
            <div style={{ marginLeft: '92px' }}>
              <BeaconChartWithLegend
                containerStyles={{ width: 640 }}
                showTitleSummary={false}
                primaryLegendProps={{
                  displayName: legendLabel
                }}
                comparisonLegendProps={{
                  displayName: legendComparisonLabel
                }}
                convertSeriesToDelimitedData={async () =>
                  saveBarChartAsDelimitedData(currentData, compareData, {
                    fileTitle: 'Review Quality',
                    metricLabel: 'Review Quality'
                  })
                }
                loading={isLoading}
                loadingComponent={<SplineChartLoading width={543} />}
              >
                <TopEntityChartV2
                  containerSx={{ width: COLUMN_CHART_WIDTH }}
                  xAxisFormatter={(xAxisCategory) =>
                    ReactDOMServer.renderToStaticMarkup(
                      <GenericXAxisLabel
                        wrapperStyle={{ wordBreak: 'break-word', width: '60px' }}
                        xAxisCategory={xAxisCategory}
                      />
                    )
                  }
                  chartSeries={[
                    {
                      type: 'column',
                      data: compareData,
                      metricType: METRICTYPE.PERCENT
                    },
                    {
                      type: 'column',
                      data: currentData,
                      metricType: METRICTYPE.PERCENT
                    }
                  ]}
                  chartOptionsOverride={{
                    chart: {
                      height: 240,
                      width: COLUMN_CHART_WIDTH,
                      marginBottom: 39,
                      spacingBottom: 0,
                      marginLeft: -14
                    }
                  }}
                  leftAlignBars={false}
                />
              </BeaconChartWithLegend>
            </div>
          </Flex>
        </BeaconChartWithLegend>
      </Flex>
    </Flex>
  );
};

export default ReviewDonutAndTopEntityChart;
